import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTheme, alpha } from "@mui/material/styles";
import {
  accentForIndex,
  adminClickableCardSx,
  adminSurfaceSx,
  adminTableWrapSx,
  adminTableHeadCellSx,
  adminTableRowSx,
  adminPageSx,
  adminToggleGroupSx,
  adminChipSx,
  MONO,
} from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import AdminSectionHeader from "./AdminSectionHeader";
import EventDetailDialog from "./EventDetailDialog";
import { adminRequest } from "../../utils/adminApi";

dayjs.extend(utc);
dayjs.extend(timezone);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const WINDOW_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

// Everything that goes into the request lives in ONE state object, and the only
// way to change a filter is `patchFilter`, which forces `page` back to 0. The
// classic pagination bug — apply a filter while sitting on page 7, land on an
// empty page — is a state shape problem, not a discipline problem: with the
// filters and the page in separate `useState`s, every new filter is one more
// place that has to remember to reset. Here it is structurally impossible.
const INITIAL_QUERY = {
  days: 30,
  type: "",
  user: "",
  page: 0,
  rowsPerPage: 50,
};

// D1-backed admin events view — the replacement for the Analytics Engine
// dashboard (AnalyticsDashboard). Reads GET /api/admin/events via the
// functions/api/admin/getEvents proxy: a typed event feed plus aggregates.
export default function EventsDashboard() {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token + routes via ?env=staging (adminRequest).
  const { env, token, envParams } = useSelector(adminRequest);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState(INITIAL_QUERY);
  const { days, type: typeFilter, user: userFilter, page, rowsPerPage } = query;
  // Two-part user filter: `userDraft` is what's in the text box, `userFilter` is
  // the COMMITTED value that actually drives the fetch. Committing on
  // Enter/blur (rather than on every keystroke) keeps one request per intent —
  // the param is an exact `user_email` match, so partial input is never a
  // useful query anyway.
  const [userDraft, setUserDraft] = useState("");
  // Row drill-in. The props column is ellipsised, which hides exactly the part of a
  // `server_error` you need to debug it (route, error name, message).
  const [selectedEvent, setSelectedEvent] = useState(null);

  // The ONLY way to change what the request selects. Always rewinds to page 0 —
  // see INITIAL_QUERY.
  const patchFilter = (patch) =>
    setQuery((q) => ({ ...q, ...patch, page: 0 }));

  const commitUserFilter = () => patchFilter({ user: userDraft.trim() });

  // Used by both the input's clear button and the "no user" reset path.
  const clearUserFilter = () => {
    setUserDraft("");
    patchFilter({ user: "" });
  };

  // Click-to-filter from the Recent events table: scope the page to the email
  // on the row that caught the admin's eye.
  const applyUserFilter = (email) => {
    setUserDraft(email);
    patchFilter({ user: email });
  };

  useEffect(() => {
    // Skip the fetch in staging mode until a staging token is held — the login
    // dialog (AdminLayout) handles auth. Firing now would send the wrong token.
    if (env === "staging" && !token) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchEvents = async () => {
      setLoading(true);
      setError(null);
      try {
        // Server-driven paging: the table shows exactly the rows the worker
        // returned for this page. Fetching a big block and slicing client-side
        // is the bug being fixed — row 201 was unreachable.
        const params = {
          days,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          ...envParams,
        };
        if (typeFilter) params.type = typeFilter;
        if (userFilter) params.user = userFilter;
        const result = await axios.get("/api/admin/getEvents", {
          params,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (!cancelled) setData(result.data);
      } catch (err) {
        console.error("Failed to fetch events:", err);
        if (!cancelled) setError("Failed to load events.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      cancelled = true;
    };
    // `query` is replaced wholesale on every filter/page change, so its identity
    // is the refetch trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, token, query]);

  const countsByType = useMemo(() => data?.countsByType ?? [], [data]);
  const events = useMemo(() => data?.events ?? [], [data]);
  const daily = useMemo(() => data?.daily ?? [], [data]);

  // `total` (rows matching the filters, ignoring limit/offset) is NEW in the
  // worker contract. Against an older deployed worker it is simply absent, and
  // we must not invent one: `events.length` is the size of the CURRENT PAGE, so
  // rendering it as the total would claim "1–50 of 50" on a table with 40k rows.
  // Instead we hand MUI count={-1}, its documented "unknown total" mode — the
  // pager renders "51–100 of more than 100" and stays fully usable. The Next
  // button is then gated on a full page having come back, which is the only
  // honest end-of-data signal available without a count.
  const total = typeof data?.total === "number" ? data.total : null;
  const hasTotal = total !== null;
  const isLastPage = events.length < rowsPerPage;

  const totalEvents = useMemo(
    () => countsByType.reduce((sum, c) => sum + (c.count ?? 0), 0),
    [countsByType]
  );

  // Map each event type to the SAME accent its stat card uses (cards colour by
  // countsByType index via accentForIndex), so a type's chart line matches its
  // card instead of the two using unrelated palettes.
  const accentByType = useMemo(() => {
    const m = new Map();
    countsByType.forEach((c, i) => m.set(c.event_type, accentForIndex(i)));
    return m;
  }, [countsByType]);

  // Pivot the flat { day, event_type, count } rows into one labelled x-axis of
  // days and one Chart.js dataset per event type.
  const chart = useMemo(() => {
    const dayset = new Set();
    const typeset = new Set();
    const byDayType = new Map();
    for (const row of daily) {
      dayset.add(row.day);
      typeset.add(row.event_type);
      byDayType.set(`${row.day}|${row.event_type}`, row.count);
    }
    const labels = Array.from(dayset).sort();
    const types = Array.from(typeset).sort();
    const datasets = types.map((type, i) => {
      const color = accentByType.get(type) ?? accentForIndex(i);
      return {
        label: type,
        data: labels.map((day) => byDayType.get(`${day}|${type}`) ?? 0),
        borderColor: color,
        backgroundColor: color,
        tension: 0.3,
        pointRadius: 2,
      };
    });
    return { labels, datasets };
  }, [daily, accentByType]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { labels: { color: theme.palette.text.secondary } },
      },
      scales: {
        x: {
          ticks: { color: theme.palette.text.secondary },
          grid: { color: alpha("#ffffff", 0.06) },
        },
        y: {
          beginAtZero: true,
          ticks: { color: theme.palette.text.secondary },
          grid: { color: alpha("#ffffff", 0.06) },
        },
      },
    }),
    [theme]
  );

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<InsightsIcon />}
          title="Events"
          // Surface the active user scope in the subtitle so a scoped view is
          // never mistaken for the global one.
          subtitle={
            userFilter
              ? `${totalEvents.toLocaleString()} events in the last ${days} days · ${userFilter}`
              : `${totalEvents.toLocaleString()} events in the last ${days} days`
          }
          actions={
            <ToggleButtonGroup
              value={days}
              exclusive
              size="small"
              onChange={(_e, v) => v && patchFilter({ days: v })}
              sx={adminToggleGroupSx(theme)}
            >
              {WINDOW_OPTIONS.map((opt) => (
                <ToggleButton key={opt.days} value={opt.days}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {countsByType.map((c, i) => {
                const active = typeFilter === c.event_type;
                const accent = accentForIndex(i);
                return (
                  <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={c.event_type}>
                    <Card
                      onClick={() =>
                        patchFilter({ type: active ? "" : c.event_type })
                      }
                      sx={adminClickableCardSx(theme, { accent, active })}
                    >
                      <CardContent>
                        <Typography
                          variant="h5"
                          sx={{ fontWeight: 700, color: accent }}
                        >
                          {(c.count ?? 0).toLocaleString()}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            wordBreak: "break-word",
                          }}
                        >
                          {c.event_type}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
              {countsByType.length === 0 && (
                <Grid size={{ xs: 12 }}>
                  <Typography sx={{ color: theme.palette.text.secondary }}>
                    No events recorded in this window yet.
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Card sx={{ ...adminSurfaceSx(theme), mb: 3 }}>
              <CardContent>
                <AdminSectionHeader
                  label="Daily events by type"
                  accent={accentForIndex(1)}
                />
                <Box sx={{ height: 320 }}>
                  {chart.labels.length > 0 ? (
                    <Line data={chart} options={chartOptions} />
                  ) : (
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                      No trend data.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <AdminSectionHeader
                  label="Recent events"
                  accent={accentForIndex(0)}
                  sx={{ mb: 2 }}
                  action={
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        size="small"
                        label="User email"
                        placeholder="exact match"
                        value={userDraft}
                        sx={{ minWidth: 240 }}
                        onChange={(e) => setUserDraft(e.target.value)}
                        onBlur={commitUserFilter}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitUserFilter();
                        }}
                        slotProps={{
                          htmlInput: { "data-testid": "events-user-filter" },
                          input: {
                            endAdornment: userDraft ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  aria-label="Clear user filter"
                                  onClick={clearUserFilter}
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          },
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="event-type-filter-label">
                          Type
                        </InputLabel>
                        <Select
                          labelId="event-type-filter-label"
                          label="Type"
                          value={typeFilter}
                          onChange={(e) => patchFilter({ type: e.target.value })}
                        >
                          <MenuItem value="">All types</MenuItem>
                          {countsByType.map((c) => (
                            <MenuItem key={c.event_type} value={c.event_type}>
                              {c.event_type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  }
                />
                <Box sx={{ ...adminTableWrapSx(theme), overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Time
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Type
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          User
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Source
                        </TableCell>
                        <TableCell sx={adminTableHeadCellSx(theme)}>
                          Props
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((ev) => (
                        <TableRow
                          key={ev.id}
                          sx={{ ...adminTableRowSx, cursor: "pointer" }}
                          hover
                          onClick={() => setSelectedEvent(ev)}
                        >
                          <TableCell
                            sx={{
                              whiteSpace: "nowrap",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {dayjs(ev.created_at).format("MMM D, HH:mm")}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={ev.event_type}
                              size="small"
                              sx={adminChipSx(
                                theme,
                                accentByType.get(ev.event_type)
                              )}
                            />
                          </TableCell>
                          <TableCell
                            // Fast path: spot an interesting event, click the
                            // email, scope the whole page to that person.
                            // stopPropagation so this doesn't also trigger the
                            // row's drill-in dialog — the two clicks overlap.
                            onClick={
                              ev.user_email
                                ? (e) => {
                                    e.stopPropagation();
                                    applyUserFilter(ev.user_email);
                                  }
                                : undefined
                            }
                            sx={{
                              color: theme.palette.text.primary,
                              ...(ev.user_email
                                ? {
                                    cursor: "pointer",
                                    "&:hover": {
                                      color: accentForIndex(0),
                                      textDecoration: "underline",
                                    },
                                  }
                                : {}),
                            }}
                          >
                            {ev.user_email || "—"}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {ev.source}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 320,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontFamily: MONO,
                              fontSize: "0.78rem",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {ev.props ? JSON.stringify(ev.props) : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                      {events.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography
                              sx={{ color: theme.palette.text.secondary, py: 2 }}
                            >
                              No events to show.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    // -1 is MUI's "unknown count" sentinel — see the `total`
                    // note above.
                    count={hasTotal ? total : -1}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    onPageChange={(_e, next) =>
                      setQuery((q) => ({ ...q, page: next }))
                    }
                    onRowsPerPageChange={(e) =>
                      // Changing page size invalidates the offset, so rewind.
                      setQuery((q) => ({
                        ...q,
                        rowsPerPage: parseInt(e.target.value, 10),
                        page: 0,
                      }))
                    }
                    // With a real count MUI disables Next on the last page
                    // itself. With count={-1} it leaves Next enabled forever,
                    // so gate it on a short page — the only end-of-data
                    // evidence available without a count.
                    slotProps={
                      hasTotal
                        ? undefined
                        : { actions: { nextButton: { disabled: isLastPage } } }
                    }
                    labelRowsPerPage="Rows"
                    // Derived from the rows actually returned, not from MUI's
                    // from/to — with count={-1} MUI assumes a full page and
                    // would report "51–100" for a 60-row tail.
                    labelDisplayedRows={() => {
                      if (events.length === 0) return hasTotal ? "0 of 0" : "0";
                      const from = page * rowsPerPage + 1;
                      const to = page * rowsPerPage + events.length;
                      const range = `${from.toLocaleString()}–${to.toLocaleString()}`;
                      return hasTotal
                        ? `${range} of ${total.toLocaleString()}`
                        : range;
                    }}
                    sx={{
                      color: theme.palette.text.secondary,
                      borderTop: `1px solid ${alpha("#ffffff", 0.08)}`,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
      <EventDetailDialog
        open={Boolean(selectedEvent)}
        event={selectedEvent}
        accent={
          selectedEvent ? accentByType.get(selectedEvent.event_type) : undefined
        }
        onClose={() => setSelectedEvent(null)}
      />
    </Box>
  );
}
